import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import CustomCalendar from '../components/CustomCalendar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TheorieClinicPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [clinicType, setClinicType] = useState('online');
  const [duration, setDuration] = useState('evening');
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    topic: '',
    location: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const INSTRUCTOR_ID = 'julie-christiansen';

  const handleSubmit = async () => {
    if (!selectedDate) {
      toast.error(t('Please select a date'));
      return;
    }

    if (!formData.client_name || !formData.client_email || !formData.client_phone || !formData.topic) {
      toast.error(t('Please fill all required fields'));
      return;
    }

    if (clinicType === 'onsite' && !formData.location) {
      toast.error(t('Please provide location for on-site clinic'));
      return;
    }

    setLoading(true);
    try {
      const clinicData = {
        instructor_id: INSTRUCTOR_ID,
        date: selectedDate.toISOString().split('T')[0],
        clinic_type: clinicType,
        duration: duration,
        language: i18n.language,
        ...formData
      };

      await axios.post(`${API}/theorie-clinics`, clinicData);
      toast.success(t('Theorie Clinic request submitted'));
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      console.error('Error submitting clinic request:', error);
      toast.error(t('Failed to submit request'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="booking-page">
        <button
          onClick={() => navigate(-1)}
          className="back-arrow-btn"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="booking-header">
          <h1 className="booking-title" data-testid="page-title">{t('Theorie Clinic with Julie')}</h1>
          <p style={{ color: '#6b6b6b' }}>{t('Book theoretical workshops on equestrian topics')}</p>
        </div>

        <div className="booking-two-column">
          {/* Left Column - Calendar */}
          <div className="booking-left">
            <CustomCalendar
              availableDates={[]}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
            />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#8b4857', textAlign: 'center', fontWeight: 500 }}>
              {t('Select your preferred date')}
            </p>
          </div>

          {/* Right Column - Information and Booking Form */}
          <div className="booking-right">
            <div style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.75rem', marginBottom: '1rem', fontFamily: "'Cormorant Garamond', serif" }}>
                {t('About Theorie Clinics')}
              </h2>
              <div style={{ color: '#6b6b6b', lineHeight: '1.7' }}>
                <p><strong>{t('Julie Christiansen - accomplished')}</strong></p>
                <br />
                <p>
                  • {t('3x World Champion')}<br />
                  • {t('3x Nordic Champion')}<br />
                  • {t('26x Danish Champion')}
                </p>
                <br />
                <p>{t('Theorie Clinic experience text')}</p>
                <br />
                <p>
                  <strong>{t('Clinic Options')}</strong><br />
                  • {t('Online evening sessions')}<br />
                  • {t('On-site workshops at your stable or club')}<br />
                  • {t('Flexible duration')}: {t('Evening, day or multi-day')}<br />
                  • {t('Topics of your choice')}
                </p>
                <br />
                <p>{t('Theorie Clinic details text')}</p>
                <br />
                <p>{t('Let us know interested')}</p>
              </div>
            </div>

            {/* Request Section */}
            <div style={{ marginTop: '2rem', marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', fontFamily: "'Cormorant Garamond', serif" }}>
                {t('Request heading')}
              </h3>
              <p style={{ color: '#6b6b6b', lineHeight: '1.7' }}>
                {t('Select dates that suit')} {t('After submitting request')}
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">{t('Clinic Type')}</label>
              <div className="payment-methods">
                <div
                  className={`payment-method ${clinicType === 'online' ? 'selected' : ''}`}
                  onClick={() => setClinicType('online')}
                  data-testid="clinic-type-online"
                >
                  {t('Online')}
                </div>
                <div
                  className={`payment-method ${clinicType === 'onsite' ? 'selected' : ''}`}
                  onClick={() => setClinicType('onsite')}
                  data-testid="clinic-type-onsite"
                >
                  {t('On-Site')}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('Preferred Duration')}</label>
              <div className="payment-methods">
                <div
                  className={`payment-method ${duration === 'evening' ? 'selected' : ''}`}
                  onClick={() => setDuration('evening')}
                  data-testid="duration-evening"
                >
                  {t('Evening')}
                </div>
                <div
                  className={`payment-method ${duration === 'day' ? 'selected' : ''}`}
                  onClick={() => setDuration('day')}
                  data-testid="duration-day"
                >
                  {t('Full Day')}
                </div>
                <div
                  className={`payment-method ${duration === 'multiday' ? 'selected' : ''}`}
                  onClick={() => setDuration('multiday')}
                  data-testid="duration-multiday"
                >
                  {t('Multi-Day')}
                </div>
              </div>
            </div>

            <div className="section-title" style={{ marginTop: '2rem' }}>{t('Your Information')}</div>
            
            <div className="form-group">
              <label className="form-label">{t('Full Name')}</label>
              <input
                type="text"
                className="form-input"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                data-testid="client-name-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('Email')}</label>
              <input
                type="email"
                className="form-input"
                value={formData.client_email}
                onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                data-testid="client-email-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('Phone')}</label>
              <input
                type="tel"
                className="form-input"
                value={formData.client_phone}
                onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                data-testid="client-phone-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('Topic / Theme')} *</label>
              <input
                type="text"
                className="form-input"
                value={formData.topic}
                onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                placeholder={t('What topic would you like to cover?')}
                data-testid="topic-input"
              />
            </div>

            {clinicType === 'onsite' && (
              <div className="form-group">
                <label className="form-label">{t('Location')} *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder={t('Stable/Club name and location')}
                  data-testid="location-input"
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{t('Additional Notes')}</label>
              <textarea
                className="form-input"
                rows="3"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t('Any special requests or questions')}
                data-testid="notes-input"
              />
            </div>

            <button
              className="cta-button"
              onClick={handleSubmit}
              disabled={loading}
              style={{ width: '100%', marginTop: '1rem', justifyContent: 'center' }}
              data-testid="submit-request-button"
            >
              {loading ? t('Submitting') : t('Request Theorie Clinic')}
            </button>

            <p style={{ fontSize: '0.875rem', color: '#6b6b6b', marginTop: '1rem', textAlign: 'center' }}>
              {t('Request only')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TheorieClinicPage;
